import { EyeOutlined } from '@ant-design/icons';
import {
  DrawerForm,
  ModalForm,
  PageContainer,
  ProFormText,
  ProFormTextArea,
} from '@ant-design/pro-components';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { App, Button, Card, Flex, Image, Input, Masonry, Popconfirm, Tag, Typography } from 'antd';
import dayjs from 'dayjs';
import { useState } from 'react';
import { useOrganizationPath } from '@/common/hooks/use-organization-path';
import { useQueryParams } from '@/common/hooks/use-query-params';
import ArticleArcPreview from '../components/ArticleArcPreview';
import ArticleImageFailureSummary from '../components/ArticleImageFailureSummary';
import {
  getIpCreatorRecord,
  queryIpCreatorRecords,
  removeIpCreatorRecord,
  updateIpCreatorRecord,
} from '../services/ip-creator';
import { getArticleImageSummary, getArticleImages } from '../utils/article-arcs';

const { Search } = Input;

const resource = 'article-arcs';
const resourceName = '文章创作';

export const renderArticleTitle = (record) => (
  <Typography.Text ellipsis={{ tooltip: record.title }}>{record.title || '-'}</Typography.Text>
);

const createMasonryItems = (dataSource) =>
  dataSource.map((record) => ({
    key: record.id,
    data: record,
  }));

const getArticleCover = (record) =>
  getArticleImages(record.images).find((image) => image.status === 'success' && image.url);

const ArticleArcs = () => {
  const [detailRecord, setDetailRecord] = useState(null);
  const [editRecord, setEditRecord] = useState(null);
  const [query, setQuery, queryString] = useQueryParams();
  const queryClient = useQueryClient();
  const { organizationId } = useOrganizationPath();
  const { message } = App.useApp();
  const title = Array.isArray(query.title) ? query.title.at(-1) : query.title || '';

  const reload = () => queryClient.invalidateQueries({ queryKey: ['ip-creator', resource] });

  const { data: listResult, isLoading: loading } = useQuery({
    queryKey: ['ip-creator', resource, organizationId, queryString],
    queryFn: () =>
      queryIpCreatorRecords(resource, {
        organizationId,
        title: title || undefined,
        current: 1,
        pageSize: 50,
      }),
  });

  const list = listResult?.data || [];

  const { mutateAsync: updateRecord, isPending: updating } = useMutation({
    mutationFn: (payload) => updateIpCreatorRecord(resource, payload),
    onSuccess: () => {
      message.success(`${resourceName}已更新`);
      reload();
    },
  });

  const { mutateAsync: removeRecord, isPending: removing } = useMutation({
    mutationFn: (payload) => removeIpCreatorRecord(resource, payload),
    onSuccess: () => {
      message.success(`${resourceName}已移除`);
      reload();
    },
    onError: () => message.error(`${resourceName}移除失败，请重试`),
  });

  const { mutateAsync: getRecord } = useMutation({
    mutationFn: (payload) => getIpCreatorRecord(resource, payload),
    onSuccess: (payload) => setDetailRecord(payload?.data || payload),
    onError: () => message.error(`${resourceName}详情读取失败，请重试`),
  });

  const updateSearchParam = (key, value) => {
    setQuery({ [key]: value || undefined });
  };

  const renderFormFields = () => (
    <>
      <ProFormText name="title" label="标题" rules={[{ required: true, message: '请输入标题' }]} />
      <ProFormTextArea
        name="article"
        label="长文正文"
        fieldProps={{ autoSize: { minRows: 12, maxRows: 24 } }}
        rules={[{ required: true, message: '请输入长文正文' }]}
      />
    </>
  );

  return (
    <PageContainer content="基于选题生成适合图文平台发布的长文章与配图">
      <Flex vertical gap="large">
        <Card variant="borderless" title={resourceName}>
          <Flex vertical gap="large">
            <Flex gap="middle" wrap>
              <Search
                allowClear
                defaultValue={title}
                key={title}
                placeholder="图文标题"
                onSearch={(value) => updateSearchParam('title', value)}
                variant="filled"
              />
            </Flex>
            {loading ? <Card loading variant="outlined" /> : null}
            {!loading && list.length === 0 ? <Card variant="outlined">暂无数据</Card> : null}
            {!loading && list.length > 0 ? (
              <Masonry
                columns={{ xs: 1, sm: 2, xl: 4 }}
                gutter={[24, 24]}
                items={createMasonryItems(list)}
                itemRender={({ data: item }) => {
                  const cover = getArticleCover(item);
                  const imageSummary = getArticleImageSummary(item.images);
                  return (
                    <Card
                      hoverable
                      cover={
                        cover ? (
                          <Image alt={cover.alt || item.title} src={cover.url} preview={false} />
                        ) : undefined
                      }
                      actions={[
                        <Button
                          key="detail"
                          type="link"
                          icon={<EyeOutlined />}
                          onClick={() => getRecord({ data: { id: item.id, organizationId } })}
                        >
                          详情
                        </Button>,
                        <Button key="edit" type="link" onClick={() => setEditRecord(item)}>
                          编辑
                        </Button>,
                        <Popconfirm
                          key="remove"
                          title={`移除${resourceName}`}
                          okText="确认"
                          cancelText="取消"
                          onConfirm={() => removeRecord({ data: { id: item.id, organizationId } })}
                        >
                          <Button type="link" danger loading={removing}>
                            移除
                          </Button>
                        </Popconfirm>,
                      ]}
                    >
                      <Card.Meta
                        title={renderArticleTitle(item)}
                        description={
                          <Flex vertical gap="small">
                            <Flex justify="space-between" gap="small">
                              <Typography.Text type="secondary">创作者</Typography.Text>
                              <Typography.Text>{item.userName || '-'}</Typography.Text>
                            </Flex>
                            <Flex justify="space-between" gap="small">
                              <Typography.Text type="secondary">更新时间</Typography.Text>
                              <Typography.Text>
                                {item.updatedAt
                                  ? dayjs(item.updatedAt).format('YYYY-MM-DD HH:mm')
                                  : '-'}
                              </Typography.Text>
                            </Flex>
                            {imageSummary.pending > 0 ? (
                              <Tag>配图等待生成 {imageSummary.pending} 张</Tag>
                            ) : null}
                            {imageSummary.generating > 0 ? (
                              <Tag color="processing">配图生成中 {imageSummary.generating} 张</Tag>
                            ) : null}
                            {imageSummary.failed > 0 ? (
                              <Tag color="warning">配图失败 {imageSummary.failed} 张</Tag>
                            ) : null}
                          </Flex>
                        }
                      />
                    </Card>
                  );
                }}
              />
            ) : null}
          </Flex>
        </Card>
      </Flex>
      <ModalForm
        key={editRecord?.id || 'edit'}
        title={`编辑${resourceName}`}
        open={Boolean(editRecord)}
        width={720}
        initialValues={editRecord || {}}
        modalProps={{
          destroyOnHidden: true,
          okButtonProps: { loading: updating },
        }}
        onOpenChange={(open) => {
          if (!open) {
            setEditRecord(null);
          }
        }}
        onFinish={async (values) => {
          try {
            await updateRecord({
              data: {
                id: editRecord.id,
                expectedUpdatedAt: editRecord.updatedAt,
                organizationId,
                title: values.title,
                article: values.article,
              },
            });
            setEditRecord(null);
            return true;
          } catch {
            return false;
          }
        }}
      >
        {renderFormFields()}
      </ModalForm>
      <DrawerForm
        title={`${resourceName}详情`}
        open={Boolean(detailRecord)}
        onOpenChange={(open) => {
          if (!open) {
            setDetailRecord(null);
          }
        }}
        submitter={false}
        width={1024}
      >
        {detailRecord ? (
          <Flex vertical gap="large">
            <ArticleImageFailureSummary images={detailRecord.images} />
            <ArticleArcPreview record={detailRecord} />
          </Flex>
        ) : null}
      </DrawerForm>
    </PageContainer>
  );
};

export default ArticleArcs;
